import { useState } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface MonthRange {
  from: Date;
  to: Date;
}

interface MonthRangePickerProps {
  monthRange: MonthRange;
  onMonthRangeChange: (range: MonthRange) => void;
  className?: string;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export function MonthRangePicker({ monthRange, onMonthRangeChange, className }: MonthRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(monthRange.from.getFullYear());
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');
  const [tempFrom, setTempFrom] = useState<Date | null>(null);
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);

  const handleMonthClick = (monthIndex: number) => {
    const selectedDate = new Date(year, monthIndex, 1);

    if (selecting === 'start') {
      const newFrom = startOfMonth(selectedDate);
      setTempFrom(newFrom);
      setSelecting('end');
    } else {
      const fromDate = tempFrom || monthRange.from;
      const newTo = endOfMonth(selectedDate);

      if (newTo < fromDate) {
        onMonthRangeChange({ from: startOfMonth(selectedDate), to: endOfMonth(fromDate) });
      } else {
        onMonthRangeChange({ from: fromDate, to: newTo });
      }

      setTempFrom(null);
      setSelecting('start');
      setHoveredMonth(null);
      setOpen(false);
    }
  };

  const isMonthInRange = (monthIndex: number) => {
    const monthDate = new Date(year, monthIndex, 15);
    return monthDate >= startOfMonth(monthRange.from) && monthDate <= endOfMonth(monthRange.to);
  };

  const isMonthInHoverRange = (monthIndex: number) => {
    if (selecting !== 'end' || !tempFrom || hoveredMonth === null) return false;

    const monthDate = new Date(year, monthIndex, 15);
    const hoverDate = new Date(year, hoveredMonth, 15);

    if (hoverDate >= tempFrom) {
      return monthDate >= tempFrom && monthDate <= endOfMonth(new Date(year, hoveredMonth, 1));
    } else {
      return monthDate >= startOfMonth(new Date(year, hoveredMonth, 1)) && monthDate <= endOfMonth(tempFrom);
    }
  };

  const isMonthStart = (monthIndex: number) => {
    if (selecting === 'end' && tempFrom) {
      return year === tempFrom.getFullYear() && monthIndex === tempFrom.getMonth();
    }
    return year === monthRange.from.getFullYear() && monthIndex === monthRange.from.getMonth();
  };

  const isMonthEnd = (monthIndex: number) => {
    return year === monthRange.to.getFullYear() && monthIndex === monthRange.to.getMonth();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setYear(monthRange.from.getFullYear());
      setSelecting('start');
      setTempFrom(null);
      setHoveredMonth(null);
    }
    setOpen(isOpen);
  };

  const formatDisplay = () => {
    const fromStr = format(monthRange.from, 'MMM yyyy');
    const toStr = format(monthRange.to, 'MMM yyyy');
    return `${fromStr} - ${toStr}`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !monthRange && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDisplay()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="end">
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground text-center">
            Select {selecting === 'start' ? 'start' : 'end'} month
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setYear(year - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold">{year}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setYear(year + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const inRange = selecting === 'start' ? isMonthInRange(index) : false;
              const inHoverRange = isMonthInHoverRange(index);
              const isStart = isMonthStart(index);
              const isEnd = selecting === 'start' ? isMonthEnd(index) : false;

              return (
                <Button
                  key={month}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-8 text-sm font-normal transition-colors",
                    inRange && "bg-primary/10",
                    inHoverRange && "bg-primary/20",
                    (isStart || isEnd) && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  )}
                  onClick={() => handleMonthClick(index)}
                  onMouseEnter={() => setHoveredMonth(index)}
                  onMouseLeave={() => setHoveredMonth(null)}
                >
                  {month}
                </Button>
              );
            })}
          </div>

          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            {tempFrom
              ? `${format(tempFrom, 'MMM yyyy')} - ...`
              : `${format(monthRange.from, 'MMM yyyy')} - ${format(monthRange.to, 'MMM yyyy')}`
            }
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}